import React, {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Spinner} from 'react-bootstrap';
import {sendAnalyticsEvent} from '../../utils';
import interiorImage from '../../interiorImage';

const MapPrintPreview = ({
    backgroundImageUrl,
    overlayImageUrl,
    fullSizeImageUrl,
    color,
    showSpinner = false,
    view = 'interior',
    frameWidth = 4961 * 0.08,
    x = 0.5,
    y = 0.33,
    isHorizontalLayout = false,
    countryName = '',
}) => {
    const {t} = useTranslation();
    const artworkRef = useRef(null);
    const frameRef = useRef(null);
    useEffect(() => {
        if (fullSizeImageUrl) artworkRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, [fullSizeImageUrl]);
    const isInterior = view === 'interior';
    const trackLargePreview = () => sendAnalyticsEvent('openLargePreview');
    useEffect(() => {
        const frame = frameRef.current;
        let animationFrame = null;
        let previousWidth = null;
        const observer = new ResizeObserver(([entry]) => {
            const width = entry.borderBoxSize?.[0]?.inlineSize ?? frame.getBoundingClientRect().width;
            if (previousWidth !== null && Math.abs(width - previousWidth) < 0.01) return;
            previousWidth = width;
            const ratio = (isHorizontalLayout ? 1.414 : 1) + 0.2;
            if (animationFrame !== null) cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(() => {
                animationFrame = null;
                frame.style.setProperty('--frame-thickness', `${width * 0.05 / ratio}px`);
            });
        });
        observer.observe(frame, {box: 'border-box'});
        return () => {
            observer.disconnect();
            if (animationFrame !== null) cancelAnimationFrame(animationFrame);
        };
    }, [isHorizontalLayout, view]);
    const artwork = (
        <div ref={frameRef} className={`poster-frame ${isHorizontalLayout ? 'landscape' : ''}`}>
            <div className="poster-mat">
                <div className="poster-artwork" style={{backgroundColor: color.main, color: color.text}} ref={artworkRef}>
                    {overlayImageUrl ? (
                        <a href={fullSizeImageUrl || overlayImageUrl} target="_blank" rel="noreferrer" aria-label={t('design.zoom')} onClick={trackLargePreview}>
                            <img className="generated-artwork" src={overlayImageUrl} alt={t('design.mapPreview', {country: countryName})}/>
                        </a>
                    ) : (
                        <div className="poster-placeholder" role="img" aria-label={t('design.mapPreview', {country: countryName})}/>
                    )}
                    {showSpinner && <div className="preview-loading" role="status" aria-label={t('generator.loading')}><Spinner animation="border" aria-hidden="true"/></div>}
                </div>
            </div>
        </div>
    );
    return (
        <div className={`map-preview ${isInterior ? 'interior-preview' : 'poster-preview'}`}>
            {isInterior ? (
                <>
                    <img className="interior-background" src={backgroundImageUrl}
                        srcSet={backgroundImageUrl === interiorImage.src ? interiorImage.srcSet : undefined}
                        sizes={interiorImage.sizes} width="1152" height="1152" loading="lazy"
                        alt={t('design.interiorAlt')}/>
                    <div className="interior-poster" style={{left: `${x * 100}%`, top: `${y * 100}%`, width: `${frameWidth / 1800 * 100}%`}}>
                        {artwork}
                    </div>
                </>
            ) : artwork}
            {fullSizeImageUrl && !showSpinner && <p className="zoom-note"><a href={fullSizeImageUrl} target="_blank" rel="noreferrer" onClick={trackLargePreview}>{t('design.zoom')}</a></p>}
        </div>
    );
};
export default MapPrintPreview;
